/**
 * SecureEmbed Tier 2 Settings Page
 * Task 1.3: Wired to backend API for per-user settings
 * Includes TOTP MFA enable/disable via direct Cognito API calls
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { User, Bell, Shield, Database, Globe, Loader2, CheckCircle, AlertCircle, X, RefreshCw } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { settingsApi, UserSettings } from '../services/api';
import { STORAGE_KEYS } from '../constants/config';

interface SettingsSectionProps {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}

const SettingsSection: React.FC<SettingsSectionProps> = ({ title, description, icon: Icon, children }) => (
  <div className="bg-gray-100 rounded-xl p-6 shadow-sm border border-gray-100">
    <div className="flex items-start gap-4 mb-6">
      <div className="p-2 bg-purple-50 rounded-lg">
        <Icon className="w-5 h-5 text-[#0369A1]" />
      </div>
      <div>
        <h3 className="font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
    </div>
    {children}
  </div>
);

// Helper: call Cognito API with the same pattern as Login.tsx
// H9: Added optional AbortSignal parameter for request cancellation
const cognitoFetch = async (target: string, body: Record<string, any>, signal?: AbortSignal) => {
  const config = window.SECUREEMBED_CONFIG;
  if (!config) throw new Error('Configuration not loaded.');
  const response = await fetch(
    `https://cognito-idp.${config.REGION}.amazonaws.com/`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}`,
      },
      body: JSON.stringify(body),
      signal,
    }
  );
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.__type || `${target} failed`);
  }
  return data;
};

const SettingsPage: React.FC = () => {
  // H9: AbortController for fetch cleanup
  const abortControllerRef = useRef<AbortController | null>(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // MFA state
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [mfaSetupSecret, setMfaSetupSecret] = useState('');
  const [mfaSetupQrUrl, setMfaSetupQrUrl] = useState('');
  const [mfaVerifyCode, setMfaVerifyCode] = useState('');
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);

  // Settings state
  const [settings, setSettings] = useState<UserSettings>({
    displayName: '',
    email: '',
    theme: 'purple',
    timezone: 'Australia/Sydney',
    dateFormat: 'DD/MM/YYYY',
    defaultTimeRange: '7d',
    exportFormat: 'json',
    notifications: {
      anomalyAlerts: true,
      dailyReports: true,
      weeklySummaries: true,
      systemAlerts: true,
    },
  });

  // Fetch MFA status from Cognito
  const fetchMfaStatus = useCallback(async () => {
    const accessToken = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (!accessToken) return;
    try {
      // H9: Use AbortController signal
      if (abortControllerRef.current) abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();
      const data = await cognitoFetch('GetUser', { AccessToken: accessToken }, abortControllerRef.current.signal);
      const mfaSettings = data.UserMFASettingList || [];
      setMfaEnabled(mfaSettings.includes('SOFTWARE_TOKEN_MFA'));
    } catch {
      // If token expired or call fails, just default to disabled display
      setMfaEnabled(false);
    }
  }, []);

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await settingsApi.getSettings();
      if (response.success && response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (err: any) {
      console.error('Failed to fetch settings:', err);
      // Use defaults if API fails (first-time user or API not deployed)
      setError(null); // Don't show error for initial load failure
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch settings on mount
  useEffect(() => {
    fetchSettings();
    fetchMfaStatus();

    // H9: Cleanup AbortController on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchMfaStatus]);

  // Handle save
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);
      await settingsApi.updateSettings(settings);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error('Failed to save settings:', err);
      setError(err.response?.data?.message || 'Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Start MFA setup: associate a software token
  const handleEnableMfa = async () => {
    const accessToken = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (!accessToken) return;

    setMfaLoading(true);
    setMfaError(null);
    try {
      const data = await cognitoFetch('AssociateSoftwareToken', { AccessToken: accessToken });
      const secretCode = data.SecretCode;

      // Get username for the QR label
      const userStr = localStorage.getItem(STORAGE_KEYS.USER);
      const username = userStr ? JSON.parse(userStr).username || 'user' : 'user';
      const qrUrl = `otpauth://totp/SecureEmbed:${username}?secret=${secretCode}&issuer=SecureEmbed`;

      setMfaSetupSecret(secretCode);
      setMfaSetupQrUrl(qrUrl);
      setShowMfaSetup(true);
    } catch (err) {
      // M13: Don't expose Cognito exception types to user
      setMfaError('An error occurred. Please try again.');
      console.error('MFA setup failed', err);
    } finally {
      setMfaLoading(false);
    }
  };

  // Verify the TOTP code and enable MFA preference
  const handleVerifyMfa = async () => {
    const accessToken = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (!accessToken) return;

    setMfaLoading(true);
    setMfaError(null);
    try {
      // Step 1: Verify the software token
      await cognitoFetch('VerifySoftwareToken', {
        AccessToken: accessToken,
        UserCode: mfaVerifyCode,
        FriendlyDeviceName: 'Authenticator App',
      });

      // Step 2: Set MFA preference to enabled + preferred
      await cognitoFetch('SetUserMFAPreference', {
        AccessToken: accessToken,
        SoftwareTokenMfaSettings: {
          Enabled: true,
          PreferredMfa: true,
        },
      });

      setMfaEnabled(true);
      setShowMfaSetup(false);
      setMfaSetupSecret('');
      setMfaSetupQrUrl('');
      setMfaVerifyCode('');
    } catch (err) {
      // M13: Don't expose Cognito exception types to user
      const message = err instanceof Error ? err.message : '';
      if (message.includes('EnableSoftwareTokenMFA') || message.includes('CodeMismatch')) {
        setMfaError('Invalid code. Please try again.');
      } else {
        setMfaError('An error occurred. Please try again.');
      }
      console.error('MFA verification failed', err);
    } finally {
      setMfaLoading(false);
    }
  };

  // Disable MFA
  const handleDisableMfa = async () => {
    const accessToken = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (!accessToken) return;

    setMfaLoading(true);
    setMfaError(null);
    try {
      await cognitoFetch('SetUserMFAPreference', {
        AccessToken: accessToken,
        SoftwareTokenMfaSettings: {
          Enabled: false,
          PreferredMfa: false,
        },
      });
      setMfaEnabled(false);
      setShowDisableConfirm(false);
    } catch (err) {
      // M13: Don't expose Cognito exception types to user
      setMfaError('An error occurred. Please try again.');
      console.error('MFA disable failed', err);
    } finally {
      setMfaLoading(false);
    }
  };

  // Update notification setting
  const updateNotification = (key: keyof NonNullable<UserSettings['notifications']>, value: boolean) => {
    setSettings(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [key]: value,
      },
    }));
  };

  const handleRefresh = useCallback(() => {
    fetchSettings();
    fetchMfaStatus();
  }, [fetchSettings, fetchMfaStatus]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Settings</h2>
          <p className="text-gray-500 mt-1">Manage your dashboard preferences</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Status indicators */}
          {error && (
            <div className="flex items-center gap-2 text-red-600 bg-red-50 px-4 py-2 rounded-lg">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}
          {success && (
            <div className="flex items-center gap-2 text-green-600 bg-green-50 px-4 py-2 rounded-lg">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm">Settings saved successfully!</span>
            </div>
          )}
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-gray-100 rounded-xl p-6 shadow-sm border border-gray-100 animate-pulse">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-9 h-9 bg-gray-200 rounded-lg" />
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-24 mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-40" />
                </div>
              </div>
              <div className="space-y-3">
                <div className="h-8 bg-gray-200 rounded" />
                <div className="h-8 bg-gray-200 rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile Settings */}
        <SettingsSection
          title="Profile"
          description="Manage your account information"
          icon={User}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
              <input
                type="text"
                value={settings.displayName || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, displayName: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={settings.email || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
        </SettingsSection>

        {/* Notification Settings */}
        <SettingsSection
          title="Notifications"
          description="Configure alert preferences"
          icon={Bell}
        >
          <div className="space-y-4">
            {[
              { key: 'anomalyAlerts', label: 'Anomaly Alerts' },
              { key: 'dailyReports', label: 'Daily Reports' },
              { key: 'weeklySummaries', label: 'Weekly Summaries' },
              { key: 'systemAlerts', label: 'System Alerts' },
            ].map((item) => (
              <div key={item.key} className="flex items-center justify-between">
                <span className="text-sm text-gray-700">{item.label}</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.notifications?.[item.key as keyof NonNullable<UserSettings['notifications']>] ?? true}
                    onChange={(e) => updateNotification(item.key as keyof NonNullable<UserSettings['notifications']>, e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-purple-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-gray-100 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0369A1]"></div>
                </label>
              </div>
            ))}
          </div>
        </SettingsSection>

        {/* Security Settings */}
        <SettingsSection
          title="Security"
          description="Manage security preferences"
          icon={Shield}
        >
          <div className="space-y-4">
            {/* MFA Error */}
            {mfaError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700">{mfaError}</p>
              </div>
            )}

            {/* MFA Setup Flow */}
            {showMfaSetup ? (
              <div className="p-4 bg-gray-50 rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-700">Set Up Two-Factor Authentication</p>
                  <button
                    onClick={() => {
                      setShowMfaSetup(false);
                      setMfaSetupSecret('');
                      setMfaSetupQrUrl('');
                      setMfaVerifyCode('');
                      setMfaError(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <p className="text-xs text-gray-500">
                  Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                </p>

                <div className="flex justify-center py-2">
                  <div className="bg-gray-100 p-3 rounded-lg shadow-sm">
                    <QRCodeSVG value={mfaSetupQrUrl} size={180} />
                  </div>
                </div>

                <div>
                  <p className="text-xs text-gray-500 mb-1">Or enter this key manually:</p>
                  <code className="block px-3 py-2 bg-gray-100 rounded text-xs font-mono text-gray-700 break-all select-all">
                    {mfaSetupSecret}
                  </code>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={mfaVerifyCode}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setMfaVerifyCode(val);
                    }}
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-center font-mono tracking-widest"
                  />
                </div>

                <button
                  onClick={handleVerifyMfa}
                  disabled={mfaLoading || mfaVerifyCode.length !== 6}
                  className="w-full px-4 py-2 bg-[#0369A1] text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {mfaLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {mfaLoading ? 'Verifying...' : 'Verify & Enable'}
                </button>
              </div>
            ) : showDisableConfirm ? (
              /* Disable MFA confirmation */
              <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                <p className="text-sm font-medium text-gray-700">Disable Two-Factor Authentication?</p>
                <p className="text-xs text-gray-500">
                  This will remove the extra layer of security from your account. You can re-enable it at any time.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleDisableMfa}
                    disabled={mfaLoading}
                    className="flex-1 px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {mfaLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                    {mfaLoading ? 'Disabling...' : 'Disable MFA'}
                  </button>
                  <button
                    onClick={() => setShowDisableConfirm(false)}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              /* Default MFA status display */
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-700">Two-Factor Authentication</p>
                  <p className="text-xs text-gray-500">
                    {mfaEnabled ? 'Enabled - Your account is protected with TOTP' : 'Add an extra layer of security'}
                  </p>
                </div>
                {mfaEnabled ? (
                  <button
                    onClick={() => { setShowDisableConfirm(true); setMfaError(null); }}
                    disabled={mfaLoading}
                    className="px-3 py-1 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50"
                  >
                    Disable
                  </button>
                ) : (
                  <button
                    onClick={handleEnableMfa}
                    disabled={mfaLoading}
                    className="px-3 py-1 text-sm bg-[#0369A1] text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 flex items-center gap-1"
                  >
                    {mfaLoading && <Loader2 className="w-3 h-3 animate-spin" />}
                    Enable
                  </button>
                )}
              </div>
            )}

          </div>
        </SettingsSection>

        {/* Data Settings */}
        <SettingsSection
          title="Data & Storage"
          description="Manage data preferences"
          icon={Database}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Time Range</label>
              <select
                value={settings.defaultTimeRange || '7d'}
                onChange={(e) => setSettings(prev => ({ ...prev, defaultTimeRange: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="1d">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Export Format</label>
              <select
                value={settings.exportFormat || 'json'}
                onChange={(e) => setSettings(prev => ({ ...prev, exportFormat: e.target.value as 'json' | 'csv' }))}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="json">JSON</option>
                <option value="csv">CSV</option>
              </select>
            </div>
          </div>
        </SettingsSection>

        {/* Regional Settings */}
        <SettingsSection
          title="Regional"
          description="Location and language preferences"
          icon={Globe}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
              <select
                value={settings.timezone || 'Australia/Sydney'}
                onChange={(e) => setSettings(prev => ({ ...prev, timezone: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="Australia/Sydney">Australia/Sydney (AEDT)</option>
                <option value="Australia/Melbourne">Australia/Melbourne (AEDT)</option>
                <option value="Australia/Brisbane">Australia/Brisbane (AEST)</option>
                <option value="UTC">UTC</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date Format</label>
              <select
                value={settings.dateFormat || 'DD/MM/YYYY'}
                onChange={(e) => setSettings(prev => ({ ...prev, dateFormat: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
              </select>
            </div>
          </div>
        </SettingsSection>
      </div>
      )}

      {/* Save button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-[#0369A1] text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default SettingsPage;
